/**
 * Base API client for communicating with the Hobbes FastAPI backend.
 * Provides authentication, error handling, and request/response management.
 */

export interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  success: boolean;
  message?: string;
  status?: number;
  code?: string;
  details?: any;
}

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: any;
}

export interface RequestOptions {
  signal?: AbortSignal;
}

export class ApiClient {
  private baseURL: string;
  private token: string | null = null;
  private onSessionExpired?: (reason: string) => void;

  constructor(baseURL: string) {
    this.baseURL = baseURL.replace(/\/$/, ''); // Remove trailing slash
  }

  /**
   * Get the configured API base URL
   */
  getBaseUrl(): string {
    return this.baseURL;
  }

  /**
   * Set the authentication token for requests
   */
  setAuthToken(token: string | null) {
    this.token = token;
  }

  /**
   * Get the current authentication token
   */
  getAuthToken(): string | null {
    return this.token;
  }

  /**
   * Set session expired callback
   */
  setSessionExpiredCallback(callback: (reason: string) => void) {
    this.onSessionExpired = callback;
  }

  /**
   * Build headers for requests
   */
  private buildHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...customHeaders,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return headers;
  }

  /**
   * Handle API response and errors
   */
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    try {
      let data: any = null;
      
      // Only parse JSON if there's content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const text = await response.text();
        if (text) {
          data = JSON.parse(text);
        }
      }

      if (!response.ok) {
        const detail = data?.detail;
        const error: ApiError = {
          message:
            (typeof detail === 'string' ? detail : detail?.message)
            || data?.message
            || `HTTP error ${response.status}`,
          status: response.status,
          code: data?.code || detail?.code,
          details: data,
        };
        
        // Handle authentication errors
        if (response.status === 401) {
          this.setAuthToken(null);
          
          // Determine the reason for 401 based on the error message
          const errorMessage = error.message.toLowerCase();
          let reason = 'Authentication failed';
          
          if (errorMessage.includes('session has expired') || errorMessage.includes('session expired')) {
            reason = 'Your session has expired. Please log in again.';
          } else if (errorMessage.includes('invalid authentication token') || errorMessage.includes('invalid token')) {
            reason = 'Invalid authentication token. Please log in again.';
          } else if (errorMessage.includes('could not validate credentials')) {
            reason = 'Authentication failed. Please log in again.';
          }
          
          // Notify about session expiry
          this.onSessionExpired?.(reason);
        }

        return {
          success: false,
          error: error.message,
          status: error.status,
          code: error.code,
          details: error.details,
        };
      }

      return {
        success: true,
        data: data,
        message: data?.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Make a GET request
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, any>,
    options?: RequestOptions,
  ): Promise<ApiResponse<T>> {
    const url = new URL(`${this.baseURL}${endpoint}`);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.buildHeaders(),
        signal: options?.signal,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Make a POST request
   */
  async post<T>(endpoint: string, data?: any, customHeaders?: Record<string, string>): Promise<ApiResponse<T>> {
    try {
      const headers = this.buildHeaders(customHeaders);
      let body: string | FormData | undefined;

      // Handle FormData (for file uploads)
      if (data instanceof FormData) {
        body = data;
        // Remove Content-Type header to let browser set it with boundary
        delete headers['Content-Type'];
      } else if (data !== undefined) {
        body = JSON.stringify(data);
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'POST',
        headers,
        body,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Make a PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PUT',
        headers: this.buildHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Make a PATCH request
   */
  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        method: 'PATCH',
        headers: this.buildHeaders(),
        body: data ? JSON.stringify(data) : undefined,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Make a DELETE request
   */
  async delete<T>(endpoint: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    try {
      const url = new URL(`${this.baseURL}${endpoint}`);

      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.append(key, String(value));
          }
        });
      }

      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers: this.buildHeaders(),
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Upload a file
   */
  async uploadFile<T>(endpoint: string, file: File, additionalData?: Record<string, any>): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
    }

    return this.post<T>(endpoint, formData);
  }
}

// Create and export the default API client instance
const apiClient = new ApiClient(
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
);

export default apiClient;
